import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { HotToastService } from '@ngneat/hot-toast';
import { Subscription } from 'rxjs';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { AccountService } from 'src/app/core/services/account.service';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { PostLoginService } from 'src/app/core/services/post-login.service';
import { ProductService } from 'src/app/core/services/product.service';
import { ModalComponent } from 'src/app/shared/components/modal/modal.component';

@Component({
  selector: 'app-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss']
})
export class LoginFormComponent implements OnInit, OnDestroy {
  @ViewChild('activationModal') activationModal: ModalComponent;
  @ViewChild('checkEmailModal') checkEmailModal: ModalComponent;

  // sign up form in proof of identity for not logged in TT
  @Input() isRegFlow: boolean;
  @Input() isAffiliateReg: boolean;

  loginForm: FormGroup;
  token: string;
  redirect: string;
  activationEmailSubscription: Subscription;
  authSubscription: Subscription;
  routerParams: Params;
  isBusinessAccount: boolean;

  get f() { return this.loginForm.controls; }

  constructor(
    private authenticationService: AuthenticationService,
    private formBuilder: FormBuilder,
    private accountService: AccountService,
    private toastService: HotToastService,
    private route: ActivatedRoute,
    private postLoginService: PostLoginService,
    private configHelper: ConfigHelper,
    private productService: ProductService
  ) { }

  ngOnInit() {
    this.route.queryParams
      .subscribe(params => {
        this.routerParams = params;
        this.token = params.unlockToken;
      });
    this.loginForm = this.formBuilder.group({
      email: ['', Validators.required],
      password: ['', Validators.required]
    });
  }

  onSubmit() {
    if (this.loginForm.invalid) {
      return;
    }
    if (!this.isAffiliateReg) {
      this.getTokenAndSetAuth();
    }
    else {
      this.accountService.checkBusinessAccountFromEmail(this.f.email.value)
        .subscribe(
          result => {
            this.isBusinessAccount = result.content.valueOf();
            if (this.isBusinessAccount == true) {
              this.toastService.warning("A Network member account cannot be used to register a test through an affiliate link. To continue your registration, please use a different email address.");
              return;
            }
            this.getTokenAndSetAuth();
          },
          (error: HttpErrorResponse) => {
            this.toastService.warning("Failed to login because of unknown problem. Please contact Paragon.");
          });
    }
  }

  getTokenAndSetAuth() {
    //getting auth token and setting authentication
    this.authSubscription = this.accountService.getAuthToken(this.f.email.value, this.f.password.value, this.token)
      .subscribe(
        result => {
          this.authenticationService.setAuthentication(result, this.f.email.value);
          if (!this.isRegFlow) {
            this.handleInstructionalProductsRedirect();
            this.postLoginService.processPostLoginAction();
          }
        },
        (error: HttpErrorResponse) => {
          if (error.error.error === "not_activated") {
            this.activationModal.open();
          } else if (error.error.error === "invalid_grant" && error.error.error_description) {
            this.toastService.warning(error.error.error_description);
          }
          else if (error.status === 400 && error.error.error_description) {
            this.toastService.warning(error.error.error_description);
          }
          else {
            this.toastService.warning("Failed to login because of unknown problem. Please contact Paragon.");
          }
        });
  }

  handleInstructionalProductsRedirect() {
    if (this.routerParams.fromip === "1") {
      if (this.routerParams.code) {
        this.productService.getInstructionalProductsUrlWithCode(this.routerParams.code)
          .subscribe(
            result => {
              const productLink = result.content;
              window.open(productLink, '_blank');
            },
            () => {
              this.toastService.warning("Failed to generate a product link");
            });
      } else {
        let productId = 0;
        if (this.routerParams.productId) {
          productId = this.routerParams.productId;
        }
        this.productService.getInstructionalProductsUrl(productId)
          .subscribe(
            result => {
              const productLink = result.content;
              window.open(productLink, '_blank');
            },
            () => {
              this.toastService.warning("Failed to generate a product link");
            });
      }
    }
  }

  sendReactivationEmail(email: string) {
    this.activationEmailSubscription = this.accountService.sendActivationEmail(email).subscribe(
      () => {
        this.checkEmailModal.open();
      },
      (error: HttpErrorResponse) => {
        if (error.status === 400) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        } else {
          this.toastService.warning("Failed to send activation email. Please contact Paragon.");
        }
      });
  }

  ngOnDestroy() {
    this.authSubscription?.unsubscribe();
    this.activationEmailSubscription?.unsubscribe();
  }

}
